/**
 * Single source of truth for assignee avatars.
 *
 * The selected-assignee trigger and the picker rows used to derive their avatar
 * separately — the trigger from `name || label || email`, the picker strictly
 * from `name` — so the same person could render with different initials and a
 * different colour depending on where you looked. Both now resolve the display
 * name the same way before deriving anything from it.
 */

const AVATAR_COLORS = [
    '#85BBF9',
    '#6366f1',
    '#8b5cf6',
    '#ec4899',
    '#f59e0b',
    '#10b981',
    '#06b6d4',
]

const NO_NAME_COLOR = '#e2e8f0'

/**
 * Assignee records reach the UI in several shapes: the picker emits
 * `{ _id, name, email, profilePicture }`, the task API may populate a user
 * document, and some option lists carry `label` instead of `name`. Resolve
 * them to one string so initials and colour always agree.
 */
export const resolveAssigneeName = (assignee) => {
    if (assignee == null) return ''
    if (typeof assignee === 'string') return assignee.trim()
    const candidate = assignee.name ?? assignee.label ?? assignee.email ?? ''
    return typeof candidate === 'string' ? candidate.trim() : ''
}

export const getAssigneeInitials = (assignee) => {
    const text = resolveAssigneeName(assignee)
    if (!text) return 'U'
    const parts = text.split(/\s+/).filter(Boolean)
    if (parts.length >= 2 && parts[0][0] && parts[1][0]) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return text.slice(0, 2).toUpperCase()
}

export const getAssigneeColor = (assignee) => {
    const text = resolveAssigneeName(assignee)
    if (!text) return NO_NAME_COLOR
    return AVATAR_COLORS[text.charCodeAt(0) % AVATAR_COLORS.length]
}
